'use strict';

const libCustomerConfigScreen = require('./CustomerConfigScreen-Provider.js');

// Fieldbook web reads `Configuration/For/Dashboards` on a project (web/fieldbook/app/scripts/app.routes.js); platform
// data operations lists the same entries to Fieldbook iOS as `Kind: Legacy`. Shape and rules:
// tools/scripts/dashboards_config/README.md.
const NAMESPACE = 'Dashboards';

/**
 * The classic dashboards built to run inside Fieldbook (injected into the page, not framed). Adding one here is an
 * engineering change: a dashboard has to be built for the embed, and only its own files may be listed, never the
 * standalone page's assets shim.
 *
 * @type {Array<{ Key: string, DashboardHash: string, DisplayName: string, Description: string, EntryPoint: string, Config: Record<string, any>, AddSample: boolean }>}
 */
const EMBEDDABLE_DASHBOARDS =
[
	{
		Key: 'MaterialLineItemSamples', DashboardHash: 'materials_samples', DisplayName: 'Sample Plan',
		Description: 'A project’s sample plan, its samples and its failing samples.',
		EntryPoint: 'dashboard.js', Config: { ObservationType: '', ShowAddSample: false }, AddSample: true,
	},
];

/**
 * @typedef {Object} FieldbookDashboardEntry
 * @property {string} Key - the entry's name in the Dashboards configuration
 * @property {Record<string, any>} Value - the entry as it will be saved
 * @property {boolean} Platform - set for every customer at Global level
 * @property {boolean} Removed
 * @property {boolean} Dirty
 */

/**
 * The entry to save. Fieldbook reads `Config.ObservationType` without a guard, and the configuration service stores an
 * empty object as nothing at all, so `Config` always keeps at least one key.
 *
 * @param {Record<string, any>} pEntry
 * @return {Record<string, any>}
 */
function normalizeEntry(pEntry)
{
	const tmpEntry = JSON.parse(JSON.stringify(pEntry || {}));
	if (!tmpEntry.Config || (typeof tmpEntry.Config !== 'object') || Array.isArray(tmpEntry.Config))
	{
		tmpEntry.Config = {};
	}
	if (!Object.prototype.hasOwnProperty.call(tmpEntry.Config, 'ObservationType'))
	{
		tmpEntry.Config.ObservationType = '';
	}
	return tmpEntry;
}

/**
 * FieldbookDashboardsConfig: the #/FieldbookDashboards screen ("Fieldbook Dashboards"). The classic dashboards a
 * customer's projects offer in Fieldbook on the web and on iPad, and whether the Sample Plan dashboard shows its New
 * Sample buttons. Who sees each one is set on Dashboard Access, so the same entitlement opens both screens.
 */
class FieldbookDashboardsConfigProvider extends libCustomerConfigScreen
{
	/** @return {Record<string, any>} */
	initialState()
	{
		return { Entries: [] };
	}

	/**
	 * @param {number} pIDCustomer
	 * @return {Promise<void>}
	 */
	async loadCustomerAsync(pIDCustomer)
	{
		const tmpState = this.state;
		const tmpMerged = await this._getJSON(`Configuration/For/${NAMESPACE}/In/Customer/${Number(pIDCustomer)}`);
		const tmpPlatform = await this._getJSON(`Configuration/For/${NAMESPACE}/In/Global/0`);
		if ((tmpMerged === null) || (tmpPlatform === null))
		{
			throw new Error('the saved dashboards could not be read');
		}
		tmpState.Entries = Object.keys(tmpMerged || {})
			.filter((pKey) => tmpMerged[pKey] && (typeof tmpMerged[pKey] === 'object') && !Array.isArray(tmpMerged[pKey]))
			.sort()
			.map((pKey) =>
			{
				this.rememberSnapshot(NAMESPACE, pKey, tmpMerged[pKey]);
				return { Key: pKey, Value: JSON.parse(JSON.stringify(tmpMerged[pKey])), Platform: !!(tmpPlatform && tmpPlatform[pKey]), Removed: false, Dirty: false };
			});
	}

	/** @return {Promise<void>} */
	async saveAsync()
	{
		const tmpState = this.state;
		for (const tmpEntry of tmpState.Entries.filter((pEntry) => pEntry.Dirty))
		{
			await this.writeCustomerConfigAsync(NAMESPACE, tmpEntry.Key, tmpEntry.Removed ? {} : normalizeEntry(tmpEntry.Value));
			tmpEntry.Dirty = false;
		}
		tmpState.Entries = tmpState.Entries.filter((pEntry) => !pEntry.Removed);
	}

	/** @return {string} */
	savedMessage()
	{
		return 'Saved. Fieldbook shows the change the next time a project is opened, within about 15 minutes.';
	}

	// --- Edits ----------------------------------------------------------------------------------------------------

	/**
	 * @param {string} pKey
	 * @return {FieldbookDashboardEntry|undefined}
	 */
	_entry(pKey)
	{
		return this.state.Entries.find((pEntry) => pEntry.Key === pKey);
	}

	/**
	 * @param {string} pKey
	 * @param {'DisplayName'|'Description'} pField
	 * @param {string} pValue
	 */
	setText(pKey, pField, pValue)
	{
		const tmpEntry = this._entry(pKey);
		if (!tmpEntry || ![ 'DisplayName', 'Description' ].includes(pField))
		{
			return;
		}
		tmpEntry.Value[pField] = String(pValue || '').trim();
		tmpEntry.Dirty = true;
		this.markDirty();
		this.render();
	}

	/**
	 * @param {string} pKey
	 * @param {boolean} pOn
	 */
	setShowAddSample(pKey, pOn)
	{
		const tmpEntry = this._entry(pKey);
		if (!tmpEntry)
		{
			return;
		}
		tmpEntry.Value = normalizeEntry(tmpEntry.Value);
		tmpEntry.Value.Config.ShowAddSample = !!pOn;
		tmpEntry.Dirty = true;
		this.markDirty();
		this.render();
	}

	/** @param {string} pKey */
	toggleRemoved(pKey)
	{
		const tmpEntry = this._entry(pKey);
		if (!tmpEntry || tmpEntry.Platform)
		{
			return;
		}
		tmpEntry.Removed = !tmpEntry.Removed;
		tmpEntry.Dirty = true;
		this.markDirty();
		this.render();
	}

	/** @param {string} pKey - an EMBEDDABLE_DASHBOARDS key */
	addDashboard(pKey)
	{
		const tmpTemplate = EMBEDDABLE_DASHBOARDS.find((pDashboard) => pDashboard.Key === pKey);
		if (!tmpTemplate)
		{
			return;
		}
		const tmpExisting = this._entry(pKey);
		if (tmpExisting)
		{
			tmpExisting.Removed = false;
		}
		else
		{
			const tmpValue = normalizeEntry({ DashboardHash: tmpTemplate.DashboardHash, DisplayName: tmpTemplate.DisplayName, Description: '', EntryPoint: tmpTemplate.EntryPoint, Config: tmpTemplate.Config });
			this.state.Entries.push({ Key: pKey, Value: tmpValue, Platform: false, Removed: false, Dirty: true });
		}
		this.markDirty();
		this.render();
	}

	// --- Rendering ------------------------------------------------------------------------------------------------

	/**
	 * @param {FieldbookDashboardEntry} pEntry
	 * @return {string}
	 */
	_entryHTML(pEntry)
	{
		const tmpKey = this._arg(pEntry.Key);
		const tmpValue = pEntry.Value || {};
		const tmpTemplate = EMBEDDABLE_DASHBOARDS.find((pDashboard) => pDashboard.DashboardHash === tmpValue.DashboardHash);
		const tmpAddSample = (tmpTemplate && tmpTemplate.AddSample) ?
			`<label class="ccs-check"><input type="checkbox" ${(tmpValue.Config && tmpValue.Config.ShowAddSample) ? 'checked' : ''} onchange="_Pict.providers.${this.providerHash}.setShowAddSample(${tmpKey}, this.checked)">
				Show the New Sample buttons. They appear only on projects that have a form type that starts a sample.</label>` :
			'';
		const tmpRemove = pEntry.Platform ?
			'<span class="ccs-sub">Set for every customer</span>' :
			`<button class="ccs-btn ${pEntry.Removed ? '' : 'is-danger'}" onclick="_Pict.providers.${this.providerHash}.toggleRemoved(${tmpKey})">${pEntry.Removed ? 'Keep' : 'Remove'}</button>`;
		if (pEntry.Removed)
		{
			return `<tr><td colspan="2"><s>${this._esc(tmpValue.DisplayName || pEntry.Key)}</s> <span class="ccs-sub ccs-warn">Removed when you save</span></td><td>${tmpRemove}</td></tr>`;
		}
		return `<tr>
			<td>
				<input class="ccs-input" type="text" value="${this._esc(tmpValue.DisplayName || '')}" aria-label="Name" onchange="_Pict.providers.${this.providerHash}.setText(${tmpKey}, 'DisplayName', this.value)">
				<span class="ccs-sub">${this._esc(tmpTemplate ? tmpTemplate.Description : (tmpValue.DashboardHash || pEntry.Key))}</span>
			</td>
			<td>${tmpAddSample}</td>
			<td>${tmpRemove}</td>
		</tr>`;
	}

	/** @return {string} the dashboards and the ones that can be added */
	bodyHTML()
	{
		const tmpState = this.state;
		const tmpRows = tmpState.Entries.map((pEntry) => this._entryHTML(pEntry)).join('');
		const tmpTable = tmpRows ?
			`<table class="ccs-table"><thead><tr><th>Name shown in Fieldbook</th><th>Options</th><th></th></tr></thead><tbody>${tmpRows}</tbody></table>` :
			'<div class="ccs-note">No dashboards, so Fieldbook doesn’t show a Dashboards menu on this customer’s projects.</div>';
		const tmpAddable = EMBEDDABLE_DASHBOARDS.filter((pDashboard) =>
		{
			const tmpEntry = this._entry(pDashboard.Key);
			return !tmpEntry || tmpEntry.Removed;
		});
		const tmpAdd = tmpAddable.map((pDashboard) => `<button class="ccs-btn" onclick="_Pict.providers.${this.providerHash}.addDashboard(${this._arg(pDashboard.Key)})"><i class="fas fa-plus"></i> ${this._esc(pDashboard.DisplayName)}</button>`).join(' ');
		return `<div class="ccs-section">
			<p class="ccs-help">These dashboards appear under Dashboards on every project in Fieldbook, on the web and on iPad. Who sees each one is set on <a href="#/DashboardAccess">Dashboard Access</a>.</p>
			${tmpTable}
			${tmpAdd ? `<div class="ccs-row"><span>Add:</span>${tmpAdd}</div>` : ''}
		</div>`;
	}
}

FieldbookDashboardsConfigProvider.SCREEN =
{
	Hash: 'FieldbookDashboardsConfig',
	StateKey: 'FieldbookDashboardsConfig',
	Mount: '#FDC-Detail',
	Entitlement: { Entity: 'DashboardAccess', Action: 'Configure', Label: 'Configure dashboard access' },
};

module.exports = FieldbookDashboardsConfigProvider;
module.exports.default_configuration = { ProviderIdentifier: 'FieldbookDashboardsConfig', AutoInitialize: true, AutoInitializeOrdinal: 0 };
module.exports.EMBEDDABLE_DASHBOARDS = EMBEDDABLE_DASHBOARDS;
module.exports.normalizeEntry = normalizeEntry;
